import { AD_DEMOS, type AdDemo } from './demos';

/**
 * Creator links (UGC pilot, 2026-11; ~/landing_pages/saddle-ads/content/ugc/creator-brief-2026-11.md).
 *
 * Each paid creator puts one short link in their bio or caption:
 *
 *   build.saddle-sites.com/go/<handle>                 e.g. /go/sarah.makes — every example (/new/start)
 *   build.saddle-sites.com/go/<audience>/<handle>      e.g. /go/volunteer/pta.sarah — one audience's demo
 *
 * which becomes, before the app renders, the same demo-first journey the ads use — their audience's demo,
 * then "What is it?", the questions and the build — with the creator as the visit's `utm_content`, so the
 * journey workbook reports every creator as its own row (scripts/journey-report.mjs `adOf`):
 *
 *   /new/idea?idea=<the demo's sentence>&utm_source=creator&utm_medium=ugc&utm_campaign=ugc_2026-11&utm_content=<handle>
 *
 * The brief's own link (/new/idea with no idea) opened a blank composer: no demo, nothing like what the
 * creator showed. Rewritten in main.tsx with history.replaceState, before the first page view is counted,
 * so the visit is read from the long address (analytics/visits.ts reads it once per tab).
 */
export const UGC_CAMPAIGN = 'ugc_2026-11';

/** The audiences a creator can be given, by the word in their link. */
const AUDIENCES: Record<string, AdDemo['ad']> = {
  volunteer: 'Volunteer',
  pta: 'Volunteer',
  wedding: 'Wedding',
  rsvp: 'Wedding',
  potluck: 'Potluck',
  trip: 'Trip',
  schedule: 'Schedule',
  golf: 'Golf',
};

const HANDLE = /^[A-Za-z0-9._-]{1,64}$/;

/** The journey address for a creator link, or null when the path is not one. */
export function creatorLinkTarget(pathname: string): string | null {
  // `/go/<handle>` (2026-11-02, no target market): the gallery of every example, tagged as theirs.
  const one = /^\/go\/([^/?#]+)\/?$/.exec(pathname);
  if (one && !AUDIENCES[one[1].toLowerCase()]) {
    let handle = '';
    try {
      handle = decodeURIComponent(one[1]).replace(/^@/, '');
    } catch {
      handle = '';
    }
    const params = new URLSearchParams({ utm_source: 'creator', utm_medium: 'ugc', utm_campaign: UGC_CAMPAIGN });
    if (HANDLE.test(handle)) params.set('utm_content', handle);
    return `/new/start?${params.toString()}`;
  }
  const m = /^\/go\/([a-z]+)(?:\/([^/?#]+))?\/?$/i.exec(pathname);
  if (!m) return null;
  const ad = AUDIENCES[m[1].toLowerCase()];
  const demo = ad ? AD_DEMOS.find((d) => d.ad === ad) : undefined;
  if (!demo) return null;
  let handle = '';
  try {
    handle = m[2] ? decodeURIComponent(m[2]).replace(/^@/, '') : '';
  } catch {
    handle = '';
  }
  const params = new URLSearchParams({ idea: demo.sentence, utm_source: 'creator', utm_medium: 'ugc', utm_campaign: UGC_CAMPAIGN });
  // A handle the event log would drop (analytics/funnel.ts PROP_PATTERNS) is left off rather than mangled.
  if (HANDLE.test(handle)) params.set('utm_content', handle);
  return `/new/idea?${params.toString()}`;
}

/** The target with the address's own parameters kept (`test=1`, `fbclid`…), the link's own winning. */
export function withOwnParams(target: string, search: string): string {
  const [path, query = ''] = target.split('?');
  const out = new URLSearchParams(query);
  for (const [k, v] of new URLSearchParams(search)) if (!out.has(k)) out.set(k, v);
  return `${path}?${out.toString()}`;
}

/** Called once in main.tsx, before the router and the first page view. */
export function rewriteCreatorLink(): void {
  try {
    const target = creatorLinkTarget(location.pathname);
    if (target) history.replaceState(history.state, '', withOwnParams(target, location.search));
  } catch {
    /* the /go address then falls through to the router, which shows the start */
  }
}
